/**
 * Keyboard-wedge scanner support. A Bluetooth or USB scanner types its code into whatever
 * has focus and then presses Enter, far faster than a person can type. Characters arriving
 * within `maxGapMs` of each other build a buffer; Enter emits it when it is long enough.
 * A human-speed gap resets the buffer, so ordinary typing never fires a scan.
 */
export function createWedge(onCode: (code: string) => void, opts: { maxGapMs?: number; minLength?: number } = {}): (e: KeyboardEvent) => void {
  const maxGap = opts.maxGapMs ?? 50;
  const minLength = opts.minLength ?? 6;
  let buf = '';
  let last = 0;
  return (e: KeyboardEvent) => {
    const t = e.target as HTMLElement | null;
    const tag = t?.tagName;
    const scanField = t?.dataset?.scanInput === 'true';
    // Keys aimed at a field belong to the field, unless it is the screen's own scan input.
    if ((tag === 'INPUT' || tag === 'TEXTAREA') && !scanField) return;
    const now = performance.now();
    if (now - last > maxGap) buf = '';
    last = now;
    if (e.key === 'Enter') {
      if (buf.length >= minLength) {
        onCode(buf);
        // Android focuses a <button> when it is tapped, and Enter on a focused button clicks it.
        // So a worker who taps Camera and then triggers the Bluetooth scanner would have the
        // scanner's own Enter switch the camera straight back off. The scan is the whole point of
        // that keystroke; nothing else gets it. The screen's own scan field is the exception — its
        // Enter has to reach the form, which is what clears the field after a manual entry.
        if (!scanField) e.preventDefault();
      }
      buf = '';
      return;
    }
    if (e.key.length === 1) buf += e.key;
  };
}
